import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DateCalenderIcon from "assets/icons/DateCalenderIcon.svg";
import DaysCalenderIcon from "assets/icons/DaysCalenderIcon.svg";
import deleteGreyIcon from "assets/icons/deleteGreyIcon.svg";
import MyDateTimePicker from "global/components/CustomDatePicker/MyDateTimePicker";
import { AppModal } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import {
  BACKEND_TIMEZONE,
  convertEstToLocalTimezone,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { RequestParticipant } from "models/interfaces";
import moment, { Moment } from "moment-timezone";
import React, { useEffect, useState } from "react";
import {
  selectScreen,
  setErrors,
  setScheduleTime,
  updateIndividualSigner,
} from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";

const DateAndTimeSelector = (props: {
  openDatePicker: boolean;
  isDelaySigner: boolean;
  setOpenDatePicker: Function;
  setIsDelaySigner: Function;
  isScheduleReq: boolean;
  setIsScheduleRequest: Function;
  signerToDelay: Partial<RequestParticipant> | Partial<RequestParticipant>[];
  isMobile?: boolean;
  // Optional external control of the days selector so callers (e.g. the mobile
  // 3-dot menu) can open "Delay by days" directly. Desktop omits these and the
  // component falls back to its own internal state.
  showDaySelector?: boolean;
  setShowDaySelector?: Function;
}) => {
  const dispatch = useAppDispatch();
  let { signRequest, errors } = useAppSelector((state: RootState) => state.app);
  const isMobile = useAppSelector(selectScreen);
  const [internalShowDaySelector, setInternalShowDaySelector] =
    useState<boolean>(false);
  const showDaySelector = props.showDaySelector ?? internalShowDaySelector;
  const setShowDaySelector = props.setShowDaySelector ?? setInternalShowDaySelector;
  const [delayDays, setDelayDays] = useState<string>("0");
  const [delayHours, setDelayHours] = useState<string>("0");

  // When opening the day selector or delay modal
  useEffect(() => {
    if (!showDaySelector) return;
    if (!props.signerToDelay) return;

    const signer = Array.isArray(props.signerToDelay)
      ? props.signerToDelay[0]
      : props.signerToDelay;

    setDelayDays(String(signer.delayDays || "0"));
    setDelayHours(String(signer.delayHours || "0"));
  }, [showDaySelector, props.signerToDelay]);

  const handleAccept = (val: any) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Sent back in the backend's zone (UTC) — see BACKEND_TIMEZONE.
    const estDateTime = moment.tz(val, userTimeZone).tz(BACKEND_TIMEZONE);
    if (val && isTruthy(estDateTime?.format("MM/DD/YYYY HH:mm:ss"))) {
      props.setOpenDatePicker(false);
      if (props.isScheduleReq) {
        if (isScheduleBeforeAlldelayedSigners(val)) {
          dispatch(setScheduleTime(estDateTime.format("MM/DD/YYYY HH:mm:ss")));
          dispatch(setErrors({ schedule: "" }));
        } else {
          openErrorNotification(
            "Date/time must precede all of the delayed signer's date/time",
          );
        }
      } else {
        delaySigner(estDateTime, val);
        dispatch(setErrors({ delay: "" }));
      }
      props.setIsDelaySigner(false);
      props.setIsScheduleRequest(false);
    }
  };

  const delaySigner = (estDateTime: Moment, val: Moment | null) => {
    const envelopeDate = signRequest.scheduleTime;
    const envelopeDateInLocalTz = convertEstToLocalTimezone(
      signRequest.scheduleTime!,
      "MM/DD/YYYY HH:mm:ss",
    );
    const updateSignerDateTime = (signer: Partial<RequestParticipant>) => {
      dispatch(
        updateIndividualSigner({
          signerId: signer.id,
          fieldsToUpdate: {
            delayDays: undefined,
            delayHours: undefined,
            scheduleTime: estDateTime.format("MM/DD/YYYY HH:mm:ss"),
          },
        }),
      );
    };
    if (Array.isArray(props.signerToDelay)) {
      // If it's a group, update each member
      props.signerToDelay.forEach((signer) => {
        if (!envelopeDate || envelopeDateInLocalTz.isBefore(val)) {
          updateSignerDateTime(signer);
        } else {
          openErrorNotification(
            "Delayed signer date/time must be after scheduled envelope date/time.",
          );
        }
      });
    } else {
      // Single signer
      if (!envelopeDate || envelopeDateInLocalTz.isBefore(val)) {
        updateSignerDateTime(
          props.signerToDelay as Partial<RequestParticipant>,
        );
      } else {
        openErrorNotification(
          "Delayed signer date/time must be after scheduled envelope date/time.",
        );
      }
    }
  };

  const isScheduleBeforeAlldelayedSigners = (envelopeDate: Moment | null) => {
    return signRequest.signers.every((signer: any) => {
      if (signer.scheduleTime) {
        return envelopeDate?.isBefore(
          convertEstToLocalTimezone(signer.scheduleTime, "MM/DD/YYYY HH:mm:ss"),
        );
      }
      return true;
    });
  };

  const datePickerBody = (
    <Box>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MyDateTimePicker handleAccept={handleAccept} />
      </LocalizationProvider>
    </Box>
  );

  const delaySignerBody = (
    <Box>
      <Stack
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"center"}
      >
        <Button
          id="delay-signer-body-date-btn"
          sx={{
            textTransform: "none",
            color: "text.primary",
          }}
          onClick={() => {
            props.setOpenDatePicker(true);
            props.setIsDelaySigner(false);
          }}
        >
          <Grid
            container
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid item xs={7}>
              <Box
                component={"img"}
                src={DateCalenderIcon}
                alt="Date calendar icon"
                aria-hidden="true"
              ></Box>
            </Grid>
            <Grid item xs={5}>
              <Typography>Date</Typography>
            </Grid>
          </Grid>
        </Button>
        <Button
          id="delay-signer-body-days-btn"
          sx={{
            textTransform: "none",
            color: "text.primary",
          }}
          onClick={() => {
            setShowDaySelector(true);
            props.setIsDelaySigner(false);
          }}
        >
          <Grid
            container
            direction={"column"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid item xs={7}>
              <Box
                component={"img"}
                src={DaysCalenderIcon}
                alt="Days calendar icon"
                aria-hidden="true"
              ></Box>
            </Grid>
            <Grid item xs={5}>
              <Typography>Days</Typography>
            </Grid>
          </Grid>
        </Button>
      </Stack>
    </Box>
  );

  const daysSelectorBody = (
    <Stack
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      spacing={4}
    >
      <Stack
        direction={"row"}
        spacing={2}
        // spacing={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }}
        width={props.isMobile ? "100%" : "70%"}
      >
        <CustomInput
          type="number"
          id="delay-days"
          placeHolder="Enter Days"
          label={"Days"}
          name={"delayDays"}
          value={delayDays}
          propsToInputElement={{ min: 0, max: 99 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
            if (raw === "") {
              setDelayDays("0");
              return;
            }
            const parsed = Number(raw);
            setDelayDays(Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : "0");
          }}
        />
        <CustomInput
          type="number"
          id="delay-hours"
          placeHolder="Enter Hours"
          label={"Hours"}
          name={"delayHours"}
          value={delayHours}
          propsToInputElement={{ min: 0, max: 99 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value.replace(/\D/g, "").slice(0, 2);
            if (raw === "") {
              setDelayHours("0");
              return;
            }
            const parsed = Number(raw);
            setDelayHours(Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : "0");
          }}
        />
      </Stack>
      <Grid container width={props.isMobile ? "100%" : "70%"} spacing={2}>
        <Grid item xs={5} display={"flex"} justifyContent={"center"}>
          <Button
            id={`datetime-selector-cancel-btn`}
            fullWidth
            size="medium"
            variant="outlined"
            onClick={() => {
              props.setOpenDatePicker(false);
              props.setIsDelaySigner(false);
              props.setIsScheduleRequest(false);
              setShowDaySelector(false);
              setDelayDays("0");
              setDelayHours("0");
            }}
            sx={{
              ...(props.isMobile && {
                "&.MuiButtonBase-root": {
                  minWidth: "unset !important",
                  minHeight: "36px !important",
                  height: "36px !important",
                  width: "auto !important",
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.625rem",
                }})
            }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item xs={7} display={"flex"} justifyContent={"center"}>
          <Button
            id={`datetime-selector-create-delay-btn`}
            fullWidth
            size="medium"
            variant="contained"
            onClick={() => {
              props.setOpenDatePicker(false);
              props.setIsDelaySigner(false);
              props.setIsScheduleRequest(false);
              setShowDaySelector(false);
              const delayFields = {
                delayDays: delayDays,
                delayHours: delayHours,
                scheduleTime: undefined,
              };

              // Handle both single signer and group
              if (Array.isArray(props.signerToDelay)) {
                props.signerToDelay.forEach((signer) => {
                  dispatch(
                    updateIndividualSigner({
                      signerId: signer.id,
                      fieldsToUpdate: delayFields,
                    }),
                  );
                });
              } else {
                dispatch(
                  updateIndividualSigner({
                    signerId: props.signerToDelay.id,
                    fieldsToUpdate: delayFields,
                  }),
                );
              }
              setDelayDays("0");
              setDelayHours("0");
            }}
            disabled={delayDays === "0" && delayHours === "0"}
            sx={{
              ...(props.isMobile && {
                "&.MuiButtonBase-root": {
                  minWidth: "unset !important",
                  minHeight: "36px !important",
                  height: "36px !important",
                  width: "auto !important",
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.625rem",
                }})
            }}
          >
            Create Delay
          </Button>
        </Grid>
      </Grid>
    </Stack>
  );

  return (
    <Stack direction={"column"} spacing={1}>
      <Stack
        direction={"column"}
        spacing={0.5}
        alignItems={"center"}
        sx={
          isTruthy(signRequest.scheduleTime)
            ? {
                position: "relative",
                top: "15px",
              }
            : {}
        }
      >
        {/* <Button
          id="schedule-envelope-btn"
          startIcon={<AccessTimeIcon />}
          sx={{
            minWidth: "220px",
            svg: {
              width: "15px",
              height: "15px",
              color: isTruthy(signRequest.scheduleTime) ? "red" : "text.primary",
            },
          }}
          onClick={() => {
            props.setOpenDatePicker(true);
            props.setIsScheduleRequest(true);
          }}
        >
          Schedule
        </Button>
        {isTruthy(signRequest.scheduleTime) && (
          <Stack
            direction={"row"}
            // spacing={1}
            // justifyContent={"center"}
            alignItems={"center"}
          >
            <Typography>
              {convertEstToLocalTimezone(
                signRequest.scheduleTime!,
                "MM/DD/YYYY HH:mm:ss"
                // false
              ).format("MMM DD, HH:mm")}
            </Typography>
            <Button
              id="delete-schedule-time-btn"
              onClick={() => {
                dispatch(setScheduleTime(undefined));
                dispatch(setErrors({ schedule: "" }));
              }}
              startIcon={
                <Box
                  component={"img"}
                  src={deleteGreyIcon}
                  alt="Delete icon"
                  aria-hidden="true"
                  sx={{
                    width: "15px",
                    height: "15px",
                  }}
                ></Box>
              }
              sx={{
                minWidth: "40px",
              }}
            ></Button>
          </Stack>
        )} */}
        {/*
          These three pickers carry no visible heading in the design, so they
          take an `ariaLabel` instead of a title — the shell's header collapses
          to just the close affordance, exactly as the Modal System intends for
          an omitted slot.
        */}
        <AppModal
          open={props.openDatePicker}
          onClose={() => {
            props.setOpenDatePicker(false);
            props.setIsDelaySigner(false);
            props.setIsScheduleRequest(false);
          }}
          ariaLabel="Select a date and time"
          size="md"
          disableBodyPadding={isMobile}
        >
          {datePickerBody}
        </AppModal>
        <AppModal
          open={props.isDelaySigner}
          onClose={() => {
            props.setIsDelaySigner(false);
          }}
          title="Delay by:"
          tone="neutral"
          size="md"
        >
          {delaySignerBody}
        </AppModal>
        <AppModal
          open={showDaySelector}
          onClose={() => {
            setShowDaySelector(false);
          }}
          ariaLabel="Select the number of days"
          size="md"
        >
          {daysSelectorBody}
        </AppModal>
      </Stack>
      {signRequest.scheduleTime && errors.schedule && (
        <FormHelperText
          error
          role="alert"
          sx={{ marginLeft: "14px !important", marginTop: "0px" }}
        >
          {errors.schedule}
        </FormHelperText>
      )}
    </Stack>
  );
};

export default DateAndTimeSelector;
